import {
  dayBookSchema,
  orderPaymentsSchema,
  outstandingPageSchema,
  paymentResultSchema,
  type DayBook,
  type OrderPayments,
  type OutstandingPage,
  type OutstandingSort,
  type PaymentResult,
  type RecordPaymentInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';

/**
 * Every payment call the app makes.
 *
 * Recording and reversing hang off the ORDER, because money is always against a bill; the two
 * money screens a shop opens daily are their own resource. That split is the API's, and this
 * module mirrors it rather than papering over it.
 *
 * Note what is absent: an "edit payment" and a "delete payment". The ledger has neither, so
 * neither is offered here — a mistake is corrected with `reversePayment`, which is a different
 * word on purpose.
 */

const ORDERS = '/orders';
const PAYMENTS = '/payments';

export function orderPaymentsPath(orderId: string): string {
  return `${ORDERS}/${orderId}/payments`;
}

export async function getOrderPayments(client: ApiClient, orderId: string): Promise<OrderPayments> {
  return await client.get(orderPaymentsPath(orderId), { schema: orderPaymentsSchema });
}

export async function recordPayment(
  client: ApiClient,
  orderId: string,
  input: RecordPaymentInput,
): Promise<PaymentResult> {
  return await client.post(orderPaymentsPath(orderId), input, { schema: paymentResultSchema });
}

export async function reversePayment(
  client: ApiClient,
  orderId: string,
  paymentId: string,
  reason: string,
): Promise<PaymentResult> {
  return await client.post(
    `${orderPaymentsPath(orderId)}/${paymentId}/reverse`,
    { reason },
    { schema: paymentResultSchema },
  );
}

export async function getDayBook(client: ApiClient, date?: string): Promise<DayBook> {
  return await client.get(`${PAYMENTS}/day-book`, {
    schema: dayBookSchema,
    ...(date ? { query: { date } } : {}),
  });
}

export interface OutstandingParams {
  sortBy?: OutstandingSort;
  q?: string;
  page?: number;
  pageSize?: number;
}

export async function getOutstanding(
  client: ApiClient,
  params: OutstandingParams = {},
): Promise<OutstandingPage> {
  return await client.get(`${PAYMENTS}/outstanding`, {
    schema: outstandingPageSchema,
    query: {
      sortBy: params.sortBy ?? 'age',
      ...(params.q ? { q: params.q } : {}),
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 25,
    },
  });
}
